import {
  BarChart3,
  Building,
  Bus,
  Calendar,
  CheckCircle,
  CoinsIcon,
  Heart,
  Home,
  MapPin,
  Target,
  TrendingUp,
  Users,
  Zap,
  Timer,
  Hourglass,
  CircleDollarSign,
  ShieldCheck,
  Trophy,
  Award,
  Wallet,
  Check,
} from "lucide-react";
import { Badge, Card, Col, Row, OverlayTrigger, Tooltip } from "react-bootstrap";
import { encodeBase64, } from "../../../../utils/config";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import type ReactECharts from "echarts-for-react";
import { getInvestorProspectusData, getInvestorProspectusProjects } from "../../project";
import { useSnackbar } from "../../../../_theme/context/SnackbarContext";
import Bar3DChart from "../../charts/Bar3DChart";
import ChartExportMenu from "../../charts/ChartExportMenu";
import CustomizedPieChart from "../../charts/CustomizedPieChart";
import { ApiResponse, IDashboardFilterData, IPBox, OutcomeBox, outcomeProject, PieChartData, PipelineSectorData, TimelineBox } from "../../core/_models";
import SimpleLoader from "../../apps/components/SimpleLoader";
import { Link, useLocation } from "react-router-dom";
import { GeoLocationPopUp } from "./geoLocationPopUp";
import { HtmlTruncateWithTooltip } from "../../apps/components/HtmlTruncateWithTooltip";
import { LogError } from "../../../../utils/GlobalLogging";
import { getApiErrorMessage } from "../../../../utils/utils";
import { isAbortedError } from "../../../../utils/global";
import { formatBudget } from "../../../../utils/formateNumber";
import SkeletonCard from "../../apps/components/skeleton/SkeletonCard";
import SectionHeader from "./SectionHeader";
import ChartSkeleton from "../../apps/components/skeleton/ChartSkeleton";

type Props = {
  filters: IDashboardFilterData
}

interface Achievement {
  id: number;
  title: string;
  description: string;
  icon: any;
  iconBg: string;
  earnedDate: string;
  projects?: string;
  bgColor: string;
}

const outcomesTypes = [
  {
    id: 1,
    icon: Home,
    color: "blue",
    gradient: "linear-gradient(to bottom right, #3b82f6, #3676e0)",
    bgGradient: "linear-gradient(to bottom right, #e5e8fb, #cfd6f8)",
  },
  {
    id: 2,
    icon: Heart,
    color: "green",
    gradient: "linear-gradient(to bottom right, #45b16a, #16a34a)",
    bgGradient: "linear-gradient(to bottom right, #e4efe6, #cee4d2)",
  },
  {
    id: 3,
    icon: MapPin,
    color: "purple",
    gradient: "linear-gradient(to bottom right, #a459ed, #9333ea)",
    bgGradient: "linear-gradient(to bottom right, #ede5fb, #e0d0f8)",
  },
  {
    id: 4,
    icon: Bus,
    color: "orange",
    gradient: "linear-gradient(to bottom right, #ed753e, #ea580c)",
    bgGradient: "linear-gradient(to bottom right, #fbe7e4, #f8d4ce)",
  },
];
const timelineDistributionStyle = [
  {
    id: 1,
    color: "#10b981",
    fill: "#10b981",
    gradient: "linear-gradient(to bottom right, #45b16a, #16a34a)",
    bgGradient: "linear-gradient(to bottom right, #e4efe6, #cee4d2)",
    icon: Zap,
  },
  {
    id: 2,
    color: "#3b82f6",
    fill: "#3b82f6",
    gradient: "linear-gradient(to bottom right, #3b82f6, #3676e0)",
    bgGradient: "linear-gradient(to bottom right, #e5e8fb, #cfd6f8)",
    icon: Timer,
  },
  {
    id: 3,
    color: "#8b5cf6",
    fill: "#8b5cf6",
    gradient: "linear-gradient(to bottom right, #a459ed, #9333ea)",
    bgGradient: "linear-gradient(to bottom right, #ede5fb, #e0d0f8)",
    icon: Hourglass,
  },
];
const pieCharStyle = [
  {
    id: 1,
    color: '#5470C6',
    shadowBlur: 20,
    shadowOffsetX: 5,
    shadowOffsetY: 5,
    shadowColor: "rgba(0, 0, 0, 0.3)"
  },
  {
    id: 2,
    color: '#EE6666',
    shadowBlur: 20,
    shadowOffsetX: 5,
    shadowOffsetY: 5,
    shadowColor: "rgba(0, 0, 0, 0.3)"
  },
  {
    id: 3,
    color: '#91CC75',
    shadowBlur: 20,
    shadowOffsetX: 5,
    shadowOffsetY: 5,
    shadowColor: "rgba(0, 0, 0, 0.3)"
  }
];
const investmentStyle = [
  {
    id: 1,
    gradient: "linear-gradient(to bottom right, #e4efe6, #cee4d2)",
    icon: <Users color="#45b16a" />
  },
  {
    id: 2,
    gradient: "linear-gradient(to bottom right, #e5e8fb, #cfd6f8)",
    icon: <Building color="#4f7eee" />
  },
  {
    id: 3,
    gradient: "linear-gradient(to bottom right, #ede5fb, #e0d0f8)",
    icon: <Target color="#a459ed" />
  },
  {
    id: 4,
    gradient: "linear-gradient(to bottom right, #fbe7e4, #f8d4ce)",
    icon: <CheckCircle color="#ed753e" />
  }
]

const achievements = [
  {
    id: 1,
    title: "Budget Approved",
    description: "First project budget approved",
    icon: CircleDollarSign,
    iconBg: "#F59E0B",
    earnedDate: "Earned Jan 2026",
    projects: "8 projects",
    bgColor: "#F8F4E8",
  },
  {
    id: 2,
    title: "Finance Secured",
    description: "Secured full project financing",
    icon: ShieldCheck,
    iconBg: "#22C55E",
    earnedDate: "Earned Feb 2026",
    ribbon: "Finance Secured",
    projects: "8 projects",
    bgColor: "#EDF8F0",
  },
  {
    id: 3,
    title: "Mega Budget",
    description: "Portfolio value exceeds R1B",
    icon: Trophy,
    iconBg: "#A855F7",
    earnedDate: "Earned Mar 2026",
    projects: "8 projects",
    bgColor: "#F7F0FD",
  },
  {
    id: 4,
    title: "Budget Master",
    description: "5+ projects budget approved",
    icon: Award,
    iconBg: "#3B82F6",
    earnedDate: "Earned Mar 2026",
    projects: "8 projects",
    bgColor: "#EEF5FF",
  },
  {
    id: 5,
    title: "Infrastructure Investor",
    description: "Attract R500M+ investment",
    icon: Wallet,
    iconBg: "#6366F1",
    earnedDate: "Earned Apr 2026",
    projects: "8 projects",
    bgColor: "#F3F3FF",
  },
];

const AchievementCard = ({ achievement }: { achievement: Achievement }) => {
  const Icon = achievement.icon;

  return (
    <div
      className="achievement-card position-relative"
      style={{ backgroundColor: achievement.bgColor }}
    >
      {/* Verified Badge */}
      <div className="verified-icon">
        <CheckCircle size={12} strokeWidth={3} />
      </div>

      {/* Main Icon */}
      <div
        className="icon-circle"
        style={{ backgroundColor: achievement.iconBg }}
      >
        <Icon size={28} color="#fff" strokeWidth={2.2} />
      </div>

      <h6 className="fw-bold mt-3 mb-1">
        {achievement.title}
      </h6>

      <p className="small text-muted mb-3">
        {achievement.description}
      </p>

      <div className="earned-badge">
        <CheckCircle size={12} className="me-1" />
        {achievement.earnedDate}
      </div>

      {achievement.projects && (
        <div className="project-badge">
          {achievement.projects}
        </div>
      )}
    </div>
  );
};

/* eslint-disable @typescript-eslint/no-explicit-any */
const InvesterProspectors: FC<Props> = ({ filters }) => {
  const location = useLocation();
  const { showSnackbar } = useSnackbar()
  const [loading, setLoading] = useState<boolean>(true);
  const [gate4Projects, setGate4Projects] = useState<any>([])
  const [boxData, setBoxData] = useState<IPBox[]>([])
  const [wcisOutcomes, setWcisOutcomes] = useState<OutcomeBox[]>([])
  const [timelineDistribution, setTimelineDistribution] = useState<TimelineBox[]>([])
  const [pieCharData, setPieCharData] = useState<PieChartData[]>([])
  const [filter, setFilter] = useState<string>("");
  const [showModal, setShowModal] = useState<boolean>(false);
  const [filteredProjects, setFilteredProjects] = useState<any>([])
  const [selectedProject, setSelectedProject] = useState<any>()
  const [periodChartData, setPeriodChartData] = useState<PipelineSectorData>({
    categories: [],
    values: [],
    colors: []
  })
  const [investmentHighlights, setInvestmentHighlights] = useState([])
  const abortControllerRef = useRef<AbortController | null>(null);
  const periodChartRef = useRef<ReactECharts>(null);
  const portfolioChartRef = useRef<ReactECharts>(null);
  const canExportPeriodChart = periodChartData.categories.length > 0 && periodChartData.values.length > 0;
  const canExportPortfolioChart = pieCharData.length > 0;

  const fetchListing = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      setLoading(true)
      const [listingResponse, ipWebPageResp] = await Promise.all([
        getInvestorProspectusProjects(filters, controller.signal),
        getInvestorProspectusData(filters, controller.signal)
      ]);

      if (controller.signal.aborted) return;

      if (ipWebPageResp.status == "ok") {
        const { three_box, outcomes, timeline_distribution, investment_highlights } = ipWebPageResp.data;
        setBoxData(three_box);
        setWcisOutcomes(outcomes.map((outcome: any) => {
          return {
            ...outcome,
            ...outcomesTypes.find(o => o.id === outcome.id)
          }
        }));

        setTimelineDistribution(timeline_distribution.map((timeline) => {
          return {
            ...timeline,
            investment: `${timeline.investment}`,
            ...timelineDistributionStyle.find(o => o.id === timeline.id)
          }
        }));
        setPieCharData(timeline_distribution.map((timeline) => {
          const itemStyle = pieCharStyle.find(o => o.id === timeline.id);
          if (itemStyle) {
            return {
              value: timeline.investment,
              name: timeline.period,
              itemStyle: { ...itemStyle }
            }
          }
          return {
            value: timeline.investment,
            name: timeline.period
          }
        }));


        setPeriodChartData({
          categories: timeline_distribution.map((timeline) => timeline.period),
          values: timeline_distribution.map((timeline) => timeline.projectCount),
          colors: timeline_distribution.map((timeline) => {
            return pieCharStyle.find(o => o.id === timeline.id)?.color || ''
          })
        })

        setInvestmentHighlights(investment_highlights.map((highlight: any) => {
          return {
            ...highlight,
            ...investmentStyle.find(o => o.id === highlight.id)
          }
        }))
      } else {
        ipWebPageResp?.message && showSnackbar(ipWebPageResp.message, 'danger');
      }


      if (listingResponse.status === 'ok') {
        setGate4Projects(listingResponse.data.projects)
      } else {
        listingResponse.message && showSnackbar(listingResponse.message, 'danger')
      }

    } catch (error) {
      if (isAbortedError(error)) return;
      showSnackbar(getApiErrorMessage(error, 'Failed to fetch investor prospectus projects'), 'danger')
      LogError("Something went wrong", error, "investor-prospectus");
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false)
      }
    }
  }, [filters, showSnackbar]);



  useEffect(() => {
    fetchListing()
    return () => {
      abortControllerRef.current?.abort();
    };
  }, [fetchListing]);

  useEffect(() => {
    if (filter) {
      const filtered = gate4Projects.filter((listing: any) =>
        listing.name?.toLowerCase().includes(filter?.toLowerCase()) ||
        (listing.eval_status && listing.eval_status?.toLowerCase().includes(filter?.toLowerCase())) ||
        listing.sector_name?.toLowerCase().includes(filter?.toLowerCase()) ||
        listing.location_name?.toLowerCase().includes(filter?.toLowerCase()) ||
        listing.department_name?.toLowerCase().includes(filter?.toLowerCase()) ||
        listing.project_manager?.toLowerCase().includes(filter?.toLowerCase())
      );
      setFilteredProjects(filtered);
    } else {
      setFilteredProjects(gate4Projects);
    }
  }, [filter, gate4Projects]);

  return (
    <div className="position-relative">
      <SectionHeader
        title="Investor Prospectus"
        description="Stage Gate 4 Projects Ready for Investment"
        icon={BarChart3}
      />
      <div className="mb-5">
        <Row className="g-3">
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="col-12 col-sm-6 col-md-4">
                <SkeletonCard
                  wrapperClassName=""
                  className="mb-4"
                  titleWidth="75%"
                  valueWidth="75%"
                  showIcon={false}
                  lines={1}
                />
              </div>
            ))
          ) : (boxData.map((box: IPBox, index) => {
            const linkUrl = `/projects/?label=${encodeBase64(box.line_one_text)}&dashboard=${encodeBase64(
              "Investor Prospectus"
            )}&url=${encodeBase64("dashboard/prospectus")}&value=${encodeBase64(
              box.count_number
            )}&filter=${encodeBase64(JSON.stringify(filters))}`;

            return (
              <Col md={4} key={box.line_one_text}>
                {index === 0 ? (
                  <Link to={linkUrl} className="text-decoration-none text-hover-investor-prospectus">
                    <Card className="apple-card">
                      <div className="p-5">
                        <div className="h1 font-bold text-gray-900 text-center">
                          {formatBudget(box.count_number)}
                        </div>
                        <p className="apple-caption h5 text-gray-600 text-center">{box.line_one_text}</p>
                        <p className="apple-caption h5 text-gray-600 text-center">{box.line_two_text}</p>
                      </div>
                    </Card>
                  </Link>
                ) : (
                  <div
                    className="cursor-default"
                  >
                    <Card className="apple-card">
                      <div className="p-5">
                        <div className="h1 font-bold text-gray-900 text-center">
                          {formatBudget(box.count_number)}
                        </div>
                        <div className="apple-caption h5 text-gray-600 text-center">
                          {box.line_one_text}
                        </div>
                        <div className="apple-caption h5 text-gray-600 text-center">
                          {box.line_two_text}
                        </div>
                      </div>
                    </Card>
                  </div>
                )}
              </Col>
            )
          }))}
        </Row>
      </div>

      <div className="apple-card border-0 shadow-sm rounded-4 p-5 h-100 mb-3">
        <div className="mb-2 d-flex justify-content-between p-3">
          <div className="d-flex align-items-start gap-4 mb-0">
            <div className="rounded-4 d-none d-sm-block">
              <Award size={25} color="#e17100" />
            </div>
            <div>
              <h1 className="apple-title d-flex align-items-center fs-2 fs-sm-1">
                Investor Prospectus Badges
              </h1>
              <p className="apple-body h-5 text-muted fw-normal fs-4 mb-0">
                Recognition for budget and finance approval milestones
              </p>
            </div>
          </div>
          <div>
            <p className="apple-title fs-1 text-primary text-center mb-0">
              5/5
            </p>
            <p className="apple-body h-5 text-muted mb-0 fw-normal fs-7 text-center">
              Badges Earned
            </p>
          </div>
        </div>

        <div className="mb-3">
          <div className="p-3 pb-2 rounded-4 d-flex justify-content-between">
            <h1 className="apple-title fs-4 mb-0">
              Badge Collection Progress
            </h1>
            <p className="apple-body h-5 text-primary fw-bold mb-0 fs-4">
              100%
            </p>
          </div>
          <div className="progress px-3">
            <div className="bg-primary rounded-pill bg-primary" style={{ width: "100%", height: "10px" }} />
          </div>
        </div>
        <div className="row g-3 p-3">
          {achievements.map((achievement) => (
            <div className="col-2" key={achievement.id}>
              <AchievementCard
                achievement={achievement}
              />
            </div>
          ))}
        </div>
        <div className="p-3">
          <div className="p-4 rounded-4 d-flex justify-content-start gap-3 bg-secondary border border-gray-300">
            <div className='today-btn text-white rounded-circle w-40px h-40px d-flex align-items-center justify-content-center'>
              <Award size={20} color="#ffffff" />
            </div>
            <div>
              <p className="fw-bold fs-4 mb-1">Badge Benefits</p>
              <p className="fw-normal fs-6 mb-0">Financial achievement badges demonstrate strong fiscal governance and attract investor confidence. Each badge unlocks enhanced visibility in investor portals and priority access to funding opportunities.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="apple-card border-0 shadow-sm rounded-4 p-5 h-100 mb-5">
        <div className="d-flex align-items-start gap-4 mb-5">
          <div className="p-3 rounded-4 d-none d-sm-block">
            <Target size={25} color="#2563eb" />
          </div>
          <div>
            <h1 className="apple-title d-flex align-items-center fs-2 fs-sm-1">
              <div className="p-0 me-2 rounded-4 d-block d-sm-none">
                <Target size={20} color="#2563eb" className="w-sm-25px h-sm-25px" />
              </div>
              WCIS Priority Outcomes
            </h1>
            <p className="apple-body h-3 text-muted fw-normal fs-5 fs-sm-1">
              Infrastructure investment aligned with Western Cape Infrastructure
              Strategy outcomes
            </p>
          </div>
        </div>
        <Row className="g-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="col-12 col-sm-6">
                <SkeletonCard
                  wrapperClassName=""
                  className="mb-4"
                  titleWidth="75%"
                  valueWidth="75%"
                  showIcon={true}
                  iconSide="left"
                  lines={2}
                />
              </div>
            ))
          ) : (wcisOutcomes.map((outcome) => (
            <Col key={outcome.id} md={6}>
              <div className="apple-card h-100 p-4">
                <div className="d-flex align-items-start gap-4">
                  {/* Icon */}
                  <div
                    className="p-3 rounded-circle shadow-apple-sm flex-shrink-0 d-none d-md-block"
                    style={{ background: outcome.bgGradient }}
                  >
                    <div
                      className="p-2 rounded-circle shadow-apple-sm"
                      style={{ background: outcome.gradient }}
                    >
                      <outcome.icon className="text-white w-15px h-5px h-sm-25px w-sm-25px" />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                      <h4 className="font-semibold text-gray-900 mb-0">
                        Outcome {outcome.id}: {outcome.title}
                      </h4>
                      <OverlayTrigger
                        placement="bottom"
                        trigger={"click"}
                        rootClose
                        overlay={
                          <Tooltip className="tooltip-size-lg text-left" id={`tooltip-${outcome.id}`}>{
                            outcome.projectInfo.map((info: outcomeProject, index: number) => (
                              <ol key={info.project_id} className="ps-0 mb-1">

                                <Link to={`/manage-project/${info.project_id}/${location?.search}`} state={{ from: location.state?.from || location.pathname, }} className="font-bold pe-2 text-decoration-none text-dark">{index + 1}. {info.name}</Link>

                                {info.description}

                              </ol>
                            ))
                          }</Tooltip>
                        }
                      >
                        <Badge
                          bg="light"
                          pill
                          className="text-primary border-primary cursor-pointer"
                        >
                          {outcome.projectCount} projects
                        </Badge>
                      </OverlayTrigger>
                    </div>

                    <p className="apple-caption text-gray-600 mt-2">
                      {outcome.description}
                    </p>

                    <div className="d-flex align-items-center gap-4 pt-3 flex-wrap">
                      <div className="d-flex align-items-center gap-2 text-gray-600">
                        <CoinsIcon className="h-4 w-4" />
                        <span className="fw-semibold">
                          {formatBudget(outcome.budget)}
                        </span>
                      </div>
                      <div className="d-flex align-items-center gap-2 text-gray-600">
                        <Building className="h-4 w-4" />
                        <span className="fw-semibold">
                          {outcome.projectCount} projects
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Col >
          )))}
        </Row >
      </div>
      <div className="apple-card border-0 shadow-sm rounded-4 p-5 h-100 mb-5">
        <div className="d-flex align-items-start justify-content-between gap-4 mb-5 py-5">
          <div className="d-flex align-items-start gap-4">
            <div className="p-3 rounded-4 d-none d-sm-block">
              <Calendar size={25} className="text-primary" />
            </div>
            <div>
              <h1 className="apple-title d-flex align-items-center fs-2 fs-sm-1">
                <div className="p-0 me-2 rounded-4 d-block d-sm-none">
                  <Calendar size={20} className="text-primary w-sm-25px h-sm-25px" />
                </div>
                Investment Timeline Distribution
              </h1>
              <p className="apple-body h-3 text-muted fw-normal fs-5 fs-sm-1">
                Project delivery across implementation timeframes
              </p>
            </div>
          </div>
          <ChartExportMenu
            fileName="investment-timeline-distribution"
            disabled={loading || (!canExportPeriodChart && !canExportPortfolioChart)}
            getCharts={() => {
              const charts = [];
              const periodChart = periodChartRef.current?.getEchartsInstance();
              const portfolioChart = portfolioChartRef.current?.getEchartsInstance();
              if (canExportPeriodChart && periodChart) {
                charts.push({ title: "Investment Distribution by Period", chart: periodChart });
              }
              if (canExportPortfolioChart && portfolioChart) {
                charts.push({ title: "Portfolio Distribution", chart: portfolioChart });
              }
              return charts;
            }}
          />
        </div>

        <Row className="g-4 mb-5">
          <Col md={6}>
            <h3 className="mb-0">Investment Distribution by Period</h3>
            <div className="p-4">
              <div style={{ width: "100%", minHeight: "275px" }}>
                {loading ? <ChartSkeleton chartTypes={["bar"]} /> : <Bar3DChart ref={periodChartRef} chartData={periodChartData} />}
              </div>
            </div>
          </Col>
          <Col md={6}>
            <h3 className="mb-0">Portfolio Distribution</h3>
            <div className="p-4 w-100">
              <div style={{ width: "100%", minHeight: "275px" }}>
                {loading ? <ChartSkeleton chartTypes={["pie"]} /> : <CustomizedPieChart ref={portfolioChartRef} series={pieCharData} />}
              </div>
            </div>
          </Col>
        </Row>
        <Row className="g-4">
          {loading ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="col-12 col-sm-6 col-md-4">
                <SkeletonCard
                  wrapperClassName=""
                  className="mb-4"
                  titleWidth="75%"
                  valueWidth="75%"
                  showIcon={false}
                  lines={3}
                />
              </div>
            ))
          ) : (timelineDistribution.map((timeline: any) => (
            <Col md={4} key={timeline.period}>
              <Link to={`/projects/?label=${encodeBase64(timeline.period)}&dashboard=${encodeBase64("Investor Prospectus")}&url=${encodeBase64('dashboard/prospectus')}&filter=${encodeBase64(JSON.stringify(filters))}`} className="text-decoration-none">
                <div className="apple-card p-5 h-100 text-center">
                  <div
                    className={`p-3 rounded-circle mx-auto`}
                    style={{
                      width: "fit-content",
                      background: timeline.bgGradient,
                    }}
                  >
                    {/* <Clock style={{ color: timeline.color }} /> */}
                    <timeline.icon style={{ color: timeline.color }} />
                  </div>

                  <h4 className="fw-semibold text-gray-900 mt-3">
                    {timeline.period}
                  </h4>
                  <p className="apple-caption text-gray-600">
                    {timeline.timeframe}
                  </p>

                  <div className="mt-3">
                    <div className="j2 fw-bold mb-1 text-gray-900">
                      {timeline.projectCount}
                    </div>
                    <div className="apple-caption mb-1 text-gray-600">
                      projects
                    </div>
                    <div className="fw-semibold mb-1 text-gray-900">
                      {formatBudget(timeline.investment)}
                    </div>
                  </div>
                </div>
              </Link>
            </Col>
          )))}
        </Row>
      </div>
      <div className="apple-card border-0 shadow-sm rounded-4 p-5 h-100 mb-5">
        <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between gap-2 gap-md-4 mb-2 mb-md-5">
          <div className="d-flex align-items-start gap-4">
            <div className="p-3 rounded-4 d-none d-sm-block">
              <CheckCircle size={25} color="#45b16a" />
            </div>
            <div>
              <h1 className="apple-title d-flex align-items-center fs-2 fs-sm-1">
                <div className="p-0 me-2 rounded-4 d-block d-sm-none">
                  <CheckCircle size={20} color="#45b16a" className="w-sm-25px h-sm-25px" />
                </div>
                Investor Ready Portfolio
              </h1>
              <p className="apple-body h-3 text-muted fw-normal fs-5 fs-sm-1">
                Current Investor ready project portfolios
              </p>
            </div>
          </div>
          <input
            type="text"
            className="form-control form-control-md mw-100 w-100 w-md-250px w-xl-300px"
            placeholder="Search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <Row className="g-4">
          {loading ? (
            Array.from({ length: 12 }).map((_, index) => (
              <div key={index} className="col-12 col-sm-6">
                <SkeletonCard
                  wrapperClassName=""
                  className="mb-4"
                  titleWidth="75%"
                  valueWidth="75%"
                  showIcon={false}
                  lines={3}
                />
              </div>
            ))
          ) : (filteredProjects.map((project: any) => {
            return <Col md={6} key={project.project_id}>
              <div className="apple-card p-4 h-100 space-y-4">
                <div className="space-y-3">
                  {/* Title & Score */}
                  <div className="d-flex justify-content-between align-items-start">
                    <h4 className="font-semibold text-gray-900 leading-tight">
                      <Link to={`/manage-project/${project.project_id}/${location?.search || ''}`} state={{ from: location.state?.from || location.pathname, }} className="text-decoration-none text-dark"> {project.name}</Link>
                    </h4>
                    <Badge className="text-success" bg="light" pill={true}>
                      {project.score}%
                    </Badge>
                  </div>

                  {/* Location & Budget */}
                  <div className="mb-2">
                    <HtmlTruncateWithTooltip html={project.inc_desc} limit={120} />
                  </div>



                  {/* Investment Status */}
                  <div className="d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-md-center pt-2">
                    <div className="d-flex flex-wrap gap-2 justify-content-between w-100 w-sm-auto">
                      {project.executiveSummary !== "#" ? <a href={`${project.executiveSummary}`} target={'_blank'} >
                        <Badge
                          className="text-success px-3 py-2"
                          bg="light"
                          pill={true}
                        >
                          Draft Prospectus
                        </Badge>
                      </a> : <Badge
                        className="text-success px-3 py-2"
                        bg="light"
                        pill={true}
                      >
                        Draft Prospectus
                      </Badge>}
                      {project.elevatorPitch != "#" ? <a href={`${project.elevatorPitch}`} target={'_blank'}>
                        <Badge
                          className="text-primary px-3 py-2"
                          bg="light"
                          pill={true}
                        >
                          Draft Elevator Pitch
                        </Badge>
                      </a> : <Badge
                        className="text-primary px-3 py-2"
                        bg="light"
                        pill={true}
                      >
                        Draft Elevator Pitch
                      </Badge>}
                    </div>
                    {project.district_name && <div className="d-flex justify-content-between w-100 w-sm-auto pt-2 pt-sm-0">
                      <div className="d-flex align-items-center gap-2 me-3 text-gray-600 cursor-pointer geolocation-btn text-bold" onClick={() => { setShowModal(true), setSelectedProject({ project_id: project.project_id, name: project.name, budget: formatBudget(project.budget) }) }}>
                        <MapPin size={16} className="geolocation-btn" />
                        <span className="fs-7 geolocation-btn">{project.district_name}</span>
                      </div>
                      <div className="d-flex align-items-center gap-2 text-gray-600">
                        <CoinsIcon size={16} />
                        <span className="fs-8 font-semibold">
                          {formatBudget(project.budget)}
                        </span>
                      </div>
                    </div>}
                    {/* <a
                      href={`${envConfig.APP_INV_URL}/investor_prospectus.php`}
                      className="apple-button-secondary rounded-pill"
                    >
                      <p className="h4 text-primary mb-0"> View Details</p>
                    </a> */}
                  </div>
                </div>
              </div>
            </Col>
          }))}
        </Row>
      </div>
      <div className="apple-card border-0 shadow-sm rounded-4 p-5 h-100 mb-5">
        <div className="d-flex align-align-items-center gap-4 mb-0 mb-md-5 py-5">
          <div className="p-3 rounded-4">
            <TrendingUp size={25} color="#45b16a" />
          </div>
          <div>
            <p className="apple-title investment-highlights-title mb-0 fw-bold">Investment Highlights</p>
          </div>
        </div>
        <Row className="g-4">
          {loading ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="col-12 col-sm-6 col-md-3">
                <SkeletonCard
                  wrapperClassName=""
                  className="mb-4"
                  titleWidth="75%"
                  valueWidth="75%"
                  showIcon={false}
                  lines={1}
                />
              </div>
            ))
          ) : (investmentHighlights.map((highlight: any) => (
            <Col key={highlight.id} className="text-center space-y-2">
              <div
                className="p-3 mb-3 rounded-circle  w-fit mx-auto"
                style={{
                  width: "fit-content",
                  background: highlight.gradient,
                }}
              >
                {highlight.icon}
              </div>
              <div className="h1 fw-bold text-gray-900">{highlight.value}</div>
              <div className="apple-caption text-gray-600">{highlight.lable}</div>
            </Col>
          )))
          }
        </Row>
      </div>
      <GeoLocationPopUp showModal={showModal} setShowModal={setShowModal} selectedProject={selectedProject} />
    </div >
  );
};

export { InvesterProspectors };
